import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { commentSettingsApi } from '../../services/api';
import { 
  PoiTypeSettingDto, 
  FeatureSettingDto, 
  FeatureSettingsResponse
} from '../../types/Comment';

const CommentSettingsPage: React.FC = () => {
  const [poiTypes, setPoiTypes] = useState<PoiTypeSettingDto[]>([]);
  const [selectedFeatureType, setSelectedFeatureType] = useState<string>('2'); // Default to DOC Huts
  const [features, setFeatures] = useState<FeatureSettingDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState<Set<string>>(new Set());
  const [bulkViewType, setBulkViewType] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const loadPoiTypes = async () => {
    try {
      setLoading(true);
      const data = await commentSettingsApi.getPoiTypeSettings();
      setPoiTypes(data);
    } catch (err) {
      console.error('Failed to load POI types', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFeatures = useCallback(async () => {
    try {
      setLoading(true);
      const response: FeatureSettingsResponse = await commentSettingsApi.getFeatureSettings(
        selectedFeatureType,
        { page, pageSize, search: searchTerm || undefined }
      );
      setFeatures(response.features);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Failed to load features', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFeatureType, page, pageSize, searchTerm]);

  // Filter and sort features based on current filters
  const filteredAndSortedFeatures = React.useMemo(() => {
    let filtered = [...features];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(feature => {
        const status = feature.commentViewType;
        if (statusFilter === 'enabled') {
          return !status || status === '';
        }
        return status === statusFilter;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string, bValue: string;
      
      if (sortBy === 'name') {
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
      } else if (sortBy === 'type') {
        aValue = a.type.toLowerCase();
        bValue = b.type.toLowerCase();
      } else {
        return 0;
      }

      if (sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return filtered;
  }, [features, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    loadPoiTypes();
  }, []);

  useEffect(() => {
    if (selectedFeatureType) {
      loadFeatures();
    }
  }, [selectedFeatureType, page, loadFeatures]);

  const handlePoiTypeUpdate = async (id: number, viewType: string | null) => {
    try {
      await commentSettingsApi.updatePoiTypeSetting(id, { 
        commentViewType: viewType === '' ? null : viewType 
      });
      await loadPoiTypes();
    } catch (err) {
      alert('Failed to update POI type setting');
      console.error(err);
    }
  };

  const handleFeatureUpdate = async (id: string, viewType: string | null) => {
    try {
      await commentSettingsApi.updateFeatureSetting(selectedFeatureType, id, { 
        commentViewType: viewType === '' ? null : viewType 
      });
      await loadFeatures();
    } catch (err) {
      alert('Failed to update feature setting');
      console.error(err);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedFeatures.size === 0) {
      alert('No features selected');
      return;
    }

    if (!bulkViewType && bulkViewType !== '') {
      alert('Please select a view type');
      return;
    }

    try {
      const featureIds = Array.from(selectedFeatures);
      const updates = featureIds.map(id => ({
        id,
        featureType: selectedFeatureType,
        commentViewType: bulkViewType === '' ? null : bulkViewType
      }));

      const result = await commentSettingsApi.bulkUpdateFeatures({ updates });
      alert(result.message);
      setSelectedFeatures(new Set());
      setBulkViewType('');
      await loadFeatures();
    } catch (err) {
      alert('Failed to bulk update features');
      console.error(err);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadFeatures();
  };

  const toggleFeatureSelection = (featureId: string) => {
    const newSelection = new Set(selectedFeatures);
    if (newSelection.has(featureId)) {
      newSelection.delete(featureId);
    } else {
      newSelection.add(featureId);
    }
    setSelectedFeatures(newSelection);
  };


  if (loading && poiTypes.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="bg-white shadow-sm rounded-xl border border-gray-200">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Loading Settings...</h3>
              <p className="mt-2 text-sm text-gray-500">
                Please wait while we fetch the comment settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <Link to="/" className="text-indigo-600 hover:text-indigo-900">
            ← Back to Dashboard
          </Link>
          <div className="mt-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Comment View Settings</h1>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">
              Configure comment visibility for different feature types and individual features
            </p>
          </div>
        </div>

        {/* Feature Type Settings */}
        <div className="bg-white shadow-sm rounded-xl border border-gray-200 mb-6 sm:mb-8 text-left">
          <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Feature Type Settings</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Global settings that apply to all features of each type unless overridden
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {poiTypes.length} Types
                </span>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid gap-4">
              {poiTypes.map((poiType) => {
                const getStatusColor = (status: string | null | undefined) => {
                  switch (status) {
                    case 'view_only': return 'bg-amber-100 text-amber-800 border-amber-200';
                    case 'disabled': return 'bg-red-100 text-red-800 border-red-200';
                    default: return 'bg-green-100 text-green-800 border-green-200';
                  }
                };
                
                const getStatusText = (status: string | null | undefined) => {
                  switch (status) {
                    case 'view_only': return 'View Only';
                    case 'disabled': return 'Disabled';
                    default: return 'Enabled';
                  }
                };
                
                return (
                  <div key={poiType.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-medium text-gray-900">{poiType.name}</h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(poiType.commentViewType)}`}>
                            {getStatusText(poiType.commentViewType)}
                          </span>
                        </div>
                        {poiType.description && (
                          <p className="text-sm text-gray-600 mt-1">{poiType.description}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <select
                          value={poiType.commentViewType || ''}
                          onChange={(e) => handlePoiTypeUpdate(poiType.id, e.target.value || null)}
                          className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        >
                          <option value="">Enabled</option>
                          <option value="view_only">View Only</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Individual Feature Settings */}
        <div className="bg-white shadow-sm rounded-xl border border-gray-200 text-left">
          <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Individual Feature Settings</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Override comment settings for specific features
                </p>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
                <svg className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Enhanced Filters */}
          {showFilters && (
            <div className="px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Feature Type</label>
                  <select
                    value={selectedFeatureType}
                    onChange={(e) => {
                      setSelectedFeatureType(e.target.value);
                      setPage(1);
                      setSelectedFeatures(new Set());
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  >
                    <option value="2">DOC Huts</option>
                    <option value="3">DOC Campsites</option>
                    <option value="4">YHA Hostels</option>
                    <option value="5">Private Campsites</option>
                    <option value="6">Water Sources</option>
                    <option value="7">Partners</option>
                    <option value="8">POIs</option>
                    <option value="9">Alerts</option>
                    <option value="10">Track Sections</option>
                    <option value="11">Bypass Sections</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search Features</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                    <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="enabled">Enabled</option>
                    <option value="view_only">View Only</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                  <div className="flex space-x-2">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    >
                      <option value="name">Name</option>
                      <option value="type">Type</option>
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <svg className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button
                  onClick={handleSearch}
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Apply Filters
                </button>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setSortBy('name');
                    setSortOrder('asc');
                    setPage(1);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
          
          {/* Quick Filters */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Quick filters:</span>
              {['all', 'enabled', 'view_only', 'disabled'].map((status) => {
                const isActive = statusFilter === status;
                const getStatusLabel = (s: string) => {
                  switch (s) {
                    case 'enabled': return 'Enabled';
                    case 'view_only': return 'View Only';
                    case 'disabled': return 'Disabled';
                    default: return 'All';
                  }
                };
                
                return (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                        : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {getStatusLabel(status)}
                    {isActive && features.length > 0 && (
                      <span className="ml-1.5 bg-indigo-200 text-indigo-800 rounded-full px-1.5 py-0.5 text-xs">
                        {(() => {
                          if (status === 'all') return features.length;
                          if (status === 'enabled') return features.filter(f => !f.commentViewType).length;
                          return features.filter(f => f.commentViewType === status).length;
                        })()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Enhanced Bulk Actions */}
          {selectedFeatures.size > 0 && (
            <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-full">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-indigo-900">
                      {selectedFeatures.size} feature{selectedFeatures.size !== 1 ? 's' : ''} selected
                    </p>
                    <p className="text-xs text-indigo-700">
                      Apply bulk actions to selected features
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={bulkViewType}
                    onChange={(e) => setBulkViewType(e.target.value)}
                    className="px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  >
                    <option value="">Select action...</option>
                    <option value="">Set to Enabled</option>
                    <option value="view_only">Set to View Only</option>
                    <option value="disabled">Set to Disabled</option>
                  </select>
                  <button
                    onClick={handleBulkUpdate}
                    disabled={!bulkViewType && bulkViewType !== ''}
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    Apply to Selected
                  </button>
                  <button
                    onClick={() => setSelectedFeatures(new Set())}
                    className="inline-flex items-center px-3 py-2 border border-indigo-300 text-indigo-700 text-sm font-medium rounded-lg hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Clear Selection
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Features Grid - Mobile Responsive */}
          <div className="px-4 sm:px-6">
            {/* Loading State for Features */}
            {loading && features.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading features...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Desktop Table Header */}
                <div className="hidden lg:block">
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-12 gap-4 items-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="col-span-1">
                        <input
                          type="checkbox"
                          checked={filteredAndSortedFeatures.length > 0 && selectedFeatures.size === filteredAndSortedFeatures.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFeatures(new Set(filteredAndSortedFeatures.map(f => f.id)));
                            } else {
                              setSelectedFeatures(new Set());
                            }
                          }}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="col-span-4">Feature Name</div>
                      <div className="col-span-2">Type</div>
                      <div className="col-span-3">Additional Info</div>
                      <div className="col-span-2">Status</div>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Features List */}
            <div className="space-y-3">
              {filteredAndSortedFeatures.length === 0 && !loading ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No features found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try adjusting your search criteria or filters.
                  </p>
                </div>
              ) : (
                filteredAndSortedFeatures.map((feature) => {
                  const isSelected = selectedFeatures.has(feature.id);
                  
                  const getStatusColor = (status: string | null | undefined) => {
                    switch (status) {
                      case 'view_only': return 'bg-amber-100 text-amber-800 border-amber-200';
                      case 'disabled': return 'bg-red-100 text-red-800 border-red-200';
                      default: return 'bg-green-100 text-green-800 border-green-200';
                    }
                  };
                  
                  const getStatusText = (status: string | null | undefined) => {
                    switch (status) {
                      case 'view_only': return 'View Only';
                      case 'disabled': return 'Disabled';
                      default: return 'Enabled';
                    }
                  };
                  
                  return (
                    <div key={feature.id} className={`bg-white rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md ${
                      isSelected ? 'border-indigo-200 bg-indigo-50' : 'border-gray-200'
                    }`}>
                      {/* Mobile Layout */}
                      <div className="lg:hidden p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleFeatureSelection(feature.id)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mt-1"
                            />
                            <div>
                              <h3 className="text-sm font-semibold text-gray-900">{feature.name}</h3>
                              <p className="text-xs text-gray-500 mt-1">{feature.type}</p>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(feature.commentViewType)}`}>
                            {getStatusText(feature.commentViewType)}
                          </span>
                        </div>
                        
                        {feature.additionalInfo && (
                          <p className="text-sm text-gray-600 mb-3 pl-7">
                            {feature.additionalInfo}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between pl-7">
                          <span className="text-sm font-medium text-gray-700">Comment View:</span>
                          <select
                            value={feature.commentViewType || ''}
                            onChange={(e) => handleFeatureUpdate(feature.id, e.target.value || null)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="">Enabled (Default)</option>
                            <option value="view_only">View Only</option>
                            <option value="disabled">Disabled</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Desktop Layout */}
                      <div className="hidden lg:block p-4">
                        <div className="grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleFeatureSelection(feature.id)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </div>
                          <div className="col-span-4">
                            <h3 className="text-sm font-semibold text-gray-900">{feature.name}</h3>
                          </div>
                          <div className="col-span-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {feature.type}
                            </span>
                          </div>
                          <div className="col-span-3">
                            <p className="text-sm text-gray-600 truncate">
                              {feature.additionalInfo || '—'}
                            </p>
                          </div>
                          <div className="col-span-2 flex items-center justify-between">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(feature.commentViewType)}`}>
                              {getStatusText(feature.commentViewType)}
                            </span>
                            <select
                              value={feature.commentViewType || ''}
                              onChange={(e) => handleFeatureUpdate(feature.id, e.target.value || null)}
                              className="ml-2 px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value="">Enabled</option>
                              <option value="view_only">View Only</option>
                              <option value="disabled">Disabled</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Enhanced Pagination */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{filteredAndSortedFeatures.length > 0 ? (page - 1) * pageSize + 1 : 0}</span> to{' '}
                  <span className="font-medium">{Math.min(page * pageSize, filteredAndSortedFeatures.length)}</span> of{' '}
                  <span className="font-medium">{filteredAndSortedFeatures.length}</span> results
                  {statusFilter !== 'all' && (
                    <span className="text-gray-500"> (filtered from {features.length} total)</span>
                  )}
                </div>
                
                <nav className="flex items-center space-x-1">
                  {/* Previous Button */}
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="hidden sm:flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      const isCurrentPage = pageNum === page;
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                            isCurrentPage
                              ? 'bg-indigo-600 text-white border border-indigo-600'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Next Button */}
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    Next
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentSettingsPage;